import { post, get } from 'app/shared/utils/fetchr';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ALERT_QUERY_KEYS } from 'app/modules/alerts/queries/keys';
import {
  InvestigationsAlertResponse,
  InvestigationsPayload,
} from 'app/modules/investigations/types';
import { useSelector } from 'react-redux';
import { selectHasReadAlertsPermission } from 'app/modules/session/selectors';
import { useEffect, useMemo, useRef } from 'react';
import { selectDLEnabledForAlertDetails } from 'app/shared/featureFlags/selectors';
import { FullAlertsCachedResponse } from 'app/modules/alerts/types/responses';
import {
  getColdStorageRetryWaitTime,
  COLD_STORAGE_MAX_RETRIES,
  getLoadingFromColdStorageState,
  hasAnyCGDOsLoading,
  isArticleLoadingResponse,
} from 'app/shared/utils/fastArchHelpers';

type AlertResponse =
  | FullAlertsCachedResponse
  | InvestigationsAlertResponse<false>;

const isLoadingResponse = (
  data: AlertResponse,
): data is FullAlertsCachedResponse => {
  return isArticleLoadingResponse(data, 'ALERT');
};

export const useGetAlert = (
  id: number,
  options: Pick<
    UseQueryOptions<InvestigationsAlertResponse<false>>,
    'enabled'
  > = {},
) => {
  const { enabled = true } = options;
  const isDLEnabledForAlertDetails = useSelector(
    selectDLEnabledForAlertDetails,
  );
  const hasReadAlertsPermission = useSelector(selectHasReadAlertsPermission);
  const dataUpdateCountRef = useRef(0);
  const query = useQuery<AlertResponse>({
    staleTime: 1000, // Prevent refetching on the first load
    refetchInterval: ({ state: { data } }) => {
      if (isDLEnabledForAlertDetails) {
        if (
          data &&
          isLoadingResponse(data) &&
          hasAnyCGDOsLoading(data.data_loader.article_ids[id]) &&
          dataUpdateCountRef.current < COLD_STORAGE_MAX_RETRIES
        ) {
          return getColdStorageRetryWaitTime(dataUpdateCountRef.current);
        }
      }
      // Do not retry by default
      return false;
    },
    enabled: hasReadAlertsPermission && enabled && id > 0,
    queryKey: ALERT_QUERY_KEYS.getAlert(id, isDLEnabledForAlertDetails),
    queryFn: () => {
      if (isDLEnabledForAlertDetails) {
        return get<FullAlertsCachedResponse>(`/v1/alerts/cached/${id}`);
      }
      return post<InvestigationsAlertResponse<false>>(
        '/v1/investigations/retrieve/alert',
        {
          short: false,
          object_type: 'ALERT',
          object_ids: [id],
        } satisfies InvestigationsPayload,
      );
    },
  });

  const coldStorageLoading = useMemo(
    () =>
      getLoadingFromColdStorageState(
        id,
        isDLEnabledForAlertDetails ? query.data?.data_loader : undefined,
      ),
    [id, query.data?.data_loader, isDLEnabledForAlertDetails],
  );

  useEffect(() => {
    // Reset update count when the alert ID changes
    dataUpdateCountRef.current = 0;
  }, [id]);

  // Increase the countRef whenever we get a loading response
  useEffect(() => {
    if (query.data && isLoadingResponse(query.data)) {
      dataUpdateCountRef.current += 1;
    }
  }, [query.data]);

  return {
    query,
    alert: query.data?.alerts[0],
    coldStorageLoading,
  };
};
